/**
 * ARRefundWorkspace.jsx
 * ERP-FIN-02 Gate AR-03: Refund Management Workspace
 * Route: /ar/refunds
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { arCreditNoteApi } from "../services/api";

const STATUS_COLORS = {
  DRAFT:              { bg: "#F3F4F6", text: "#374151" },
  PENDING_APPROVAL:   { bg: "#FEF3C7", text: "#92400E" },
  APPROVED:           { bg: "#DBEAFE", text: "#1D4ED8" },
  SETTLEMENT_PENDING: { bg: "#FEF3C7", text: "#B45309" },
  SETTLED:            { bg: "#D1FAE5", text: "#065F46" },
  REJECTED:           { bg: "#FEE2E2", text: "#991B1B" },
  CANCELLED:          { bg: "#F3F4F6", text: "#6B7280" },
};

function StatusPill({ status }) {
  const c = STATUS_COLORS[status] || { bg: "#F3F4F6", text: "#374151" };
  return (
    <span style={{
      background: c.bg, color: c.text, borderRadius: "12px",
      padding: "2px 10px", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

function fmtAmt(minor, currency = "INR") {
  const v = (minor || 0) / 100;
  return currency === "INR"
    ? `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
    : `${currency} ${v.toFixed(2)}`;
}

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "DRAFT", label: "Draft" },
  { key: "PENDING_APPROVAL", label: "Pending Approval" },
  { key: "SETTLEMENT_PENDING", label: "Settlement Pending" },
  { key: "SETTLED", label: "Settled" },
  { key: "REJECTED", label: "Rejected" },
  { key: "CANCELLED", label: "Cancelled" },
];

export default function ARRefundWorkspace() {
  const { user }  = useAuth();
  const navigate  = useNavigate();

  const [activeTab,  setActiveTab]  = useState("");
  const [refunds,    setRefunds]    = useState([]);
  const [total,      setTotal]      = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [selected,   setSelected]   = useState(null);
  const [actionErr,  setActionErr]  = useState(null);

  const canApprove = user?.capabilities?.includes("ar.refund.approve");
  const canSettle  = user?.capabilities?.includes("ar.refund.settle");

  const loadRefunds = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: 1, page_size: 50 };
      if (activeTab) params.status = activeTab;
      const res = await arCreditNoteApi.listRefunds(params);
      setRefunds(res.data.refunds || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load refunds");
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { loadRefunds(); }, [loadRefunds]);

  async function handleAction(rfId, action, payload) {
    setActionErr(null);
    try {
      if (action === "submit")   await arCreditNoteApi.submitRefund(rfId, {});
      if (action === "approve")  await arCreditNoteApi.approveRefund(rfId, payload || {});
      if (action === "reject")   await arCreditNoteApi.rejectRefund(rfId, payload);
      if (action === "cancel")   await arCreditNoteApi.cancelRefund(rfId, payload);
      if (action === "settle")   await arCreditNoteApi.settleRefund(rfId, payload);
      setSelected(null);
      await loadRefunds();
    } catch (e) {
      setActionErr(e.response?.data?.detail || e.message || "Action failed");
    }
  }

  return (
    <div data-testid="ar-refund-workspace" style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px" }}>
        <div>
          <button onClick={() => navigate("/ar/credit-notes")} style={backBtn}>← Credit Notes</button>
          <h1 style={{ fontSize: "24px", fontWeight: 700, color: "#111827", margin: 0 }}>Refund Requests</h1>
          <p style={{ color: "#6B7280", fontSize: "14px", margin: "4px 0 0" }}>{total} refund request{total !== 1 ? "s" : ""}</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "16px", flexWrap: "wrap" }}>
        {STATUS_TABS.map(tab => (
          <button
            key={tab.key}
            data-testid={`refund-tab-${tab.key || "all"}`}
            onClick={() => setActiveTab(tab.key)}
            style={{
              ...tabBtn,
              background:  activeTab === tab.key ? "#111827" : "#F9FAFB",
              color:       activeTab === tab.key ? "#fff"    : "#374151",
              borderColor: activeTab === tab.key ? "#111827" : "#E5E7EB",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {actionErr && <div style={errorBox}>{actionErr}</div>}
      {error     && <div style={errorBox}>{error}</div>}

      {!loading && (
        <div style={{ background: "#fff", borderRadius: "8px", border: "1px solid #E5E7EB", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#F9FAFB" }}>
                {["Refund Number", "Customer", "Amount", "Method", "Status", "Created", "Actions"].map((h, i) => (
                  <th key={i} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {refunds.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: "48px", color: "#9CA3AF" }}>No refund requests found</td></tr>
              ) : refunds.map(rf => (
                <tr key={rf.id} data-testid={`refund-row-${rf.id}`}>
                  <td style={tdStyle}><span style={{ fontFamily: "monospace", fontSize: "13px" }}>{rf.refund_number}</span></td>
                  <td style={tdStyle}>{rf.payee_name}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>{fmtAmt(rf.refund_amount_minor, rf.currency_code)}</td>
                  <td style={tdStyle}>{rf.refund_method}</td>
                  <td style={tdStyle}><StatusPill status={rf.status} /></td>
                  <td style={tdStyle}>{(rf.created_at || "").slice(0, 10)}</td>
                  <td style={{ ...tdStyle, display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    {rf.status === "DRAFT" && (
                      <button data-testid={`submit-refund-${rf.id}`} onClick={() => handleAction(rf.id, "submit")} style={actBtn}>Submit</button>
                    )}
                    {rf.status === "PENDING_APPROVAL" && canApprove && rf.created_by !== user?.id && (
                      <>
                        <button data-testid={`approve-refund-${rf.id}`} onClick={() => handleAction(rf.id, "approve")} style={{ ...actBtn, background: "#D1FAE5", color: "#065F46" }}>Approve</button>
                        <button data-testid={`reject-refund-${rf.id}`} onClick={() => {
                          const reason = window.prompt("Rejection reason:");
                          if (reason) handleAction(rf.id, "reject", { rejection_reason: reason });
                        }} style={{ ...actBtn, background: "#FEE2E2", color: "#991B1B" }}>Reject</button>
                      </>
                    )}
                    {rf.status === "SETTLEMENT_PENDING" && canSettle && (
                      <button data-testid={`settle-refund-${rf.id}`} onClick={() => setSelected({ rf, action: "settle" })} style={{ ...actBtn, background: "#DBEAFE", color: "#1D4ED8" }}>Record Settlement</button>
                    )}
                    {!["SETTLED", "CANCELLED"].includes(rf.status) && (
                      <button data-testid={`cancel-refund-${rf.id}`} onClick={() => {
                        const reason = window.prompt("Cancellation reason:");
                        if (reason) handleAction(rf.id, "cancel", { cancellation_reason: reason });
                      }} style={actBtn}>Cancel</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading && <div style={{ textAlign: "center", padding: "48px", color: "#6B7280" }}>Loading…</div>}

      {/* Settlement Modal */}
      {selected?.action === "settle" && (
        <SettleModal
          rf={selected.rf}
          onClose={() => setSelected(null)}
          onSettle={async (evidence, notes, date) => {
            await handleAction(selected.rf.id, "settle", { settlement_evidence: evidence, settlement_notes: notes, settlement_date: date });
          }}
        />
      )}
    </div>
  );
}

function SettleModal({ rf, onClose, onSettle }) {
  const [evidence, setEvidence] = useState("");
  const [notes,    setNotes]    = useState("");
  const [date,     setDate]     = useState(new Date().toISOString().slice(0, 10));
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!evidence.trim()) { setError("Settlement evidence (UTR/ref) is required"); return; }
    setLoading(true);
    try { await onSettle(evidence.trim(), notes.trim(), date); }
    catch (err) { setError(err.message || "Failed"); setLoading(false); }
  }

  return (
    <div style={modalOverlay}>
      <div style={{ ...modalBox, width: "440px" }} data-testid="settle-modal">
        <h2 style={{ margin: "0 0 4px", fontSize: "17px", fontWeight: 700 }}>Record Settlement Evidence</h2>
        <p style={{ color: "#6B7280", fontSize: "13px", marginBottom: "16px" }}>
          Refund: <strong>{rf.refund_number}</strong> · {fmtAmt(rf.refund_amount_minor, rf.currency_code)}
        </p>
        {error && <div style={errorBox}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={formRow}><label style={labelStyle}>Settlement Reference (UTR / Cheque No.)</label>
            <input data-testid="settlement-evidence-input" style={inputStyle} value={evidence} onChange={e => setEvidence(e.target.value)} placeholder="e.g. NEFT123456789012" required />
          </div>
          <div style={formRow}><label style={labelStyle}>Settlement Date</label>
            <input style={inputStyle} type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div style={formRow}><label style={labelStyle}>Notes (optional)</label>
            <textarea style={{ ...inputStyle, height: "60px", resize: "vertical" }} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={btnOutline}>Cancel</button>
            <button data-testid="settle-confirm-btn" type="submit" disabled={loading} style={btnPrimary}>{loading ? "Saving…" : "Record Settlement"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const btnPrimary  = { background: "#111827", color: "#fff", border: "none", borderRadius: "6px", padding: "8px 16px", fontSize: "14px", cursor: "pointer", fontWeight: 600 };
const btnOutline  = { background: "#fff", color: "#374151", border: "1px solid #D1D5DB", borderRadius: "6px", padding: "8px 16px", fontSize: "14px", cursor: "pointer" };
const backBtn     = { background: "none", border: "none", cursor: "pointer", color: "#6B7280", fontSize: "14px", marginBottom: "8px", padding: "0", display: "block" };
const tabBtn      = { border: "1px solid", borderRadius: "6px", padding: "4px 12px", fontSize: "13px", cursor: "pointer", fontWeight: 500 };
const actBtn      = { background: "#F3F4F6", color: "#374151", border: "none", borderRadius: "4px", padding: "4px 10px", fontSize: "12px", cursor: "pointer" };
const thStyle     = { padding: "10px 14px", fontSize: "12px", fontWeight: 600, color: "#6B7280", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" };
const tdStyle     = { padding: "12px 14px", fontSize: "14px", color: "#111827", borderTop: "1px solid #F3F4F6" };
const errorBox    = { background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", borderRadius: "6px", padding: "10px 14px", marginBottom: "12px", fontSize: "14px" };
const modalOverlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalBox    = { background: "#fff", borderRadius: "12px", padding: "24px", maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" };
const formRow     = { marginBottom: "14px" };
const labelStyle  = { display: "block", fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "4px" };
const inputStyle  = { width: "100%", border: "1px solid #D1D5DB", borderRadius: "6px", padding: "8px 10px", fontSize: "14px", boxSizing: "border-box" };
